import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Combobox } from '@/components/ui/combobox';
import { Field, FieldGroup, FieldRow } from '@/components/ui/field';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValueText } from '@/components/ui/select';
import { CreateVisitTypeDto, FormDto, RevenueCodeDto, ServiceCodeDto, UpdateVisitTypeDto, VisitTypeDto } from '@/lib/ServerApi';
import { api } from '@/lib/api';
import { CheckboxGroup, createListCollection, Fieldset, Flex, Grid, GridItem, Input, Text } from '@chakra-ui/react';
import { Button } from '@/components/ui/button';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { Icon } from '@chakra-ui/react';
import { DollarSign } from 'lucide-react';
import { InputGroup } from '@/components/ui/input-group';
import { NumberInputField, NumberInputRoot } from '@/components/ui/number-input';

const validationSchema = z
    .object({
        type: z.string().min(1, 'Type is required'),
        title: z.string().min(1, 'Title is required'),
        status: z.string().min(1, 'Status is required'),
        discipline: z.string().min(1, 'Discipline is required'),
        employeeClass: z.string().min(1, 'Employee class is required'),
        revenueCodeId: z.string().nullish(),
        serviceCodeId: z.string().nullish(),
        defaultPay: z.number().nullish(),
        defaultHours: z.number().nullish(),
        adminCost: z.number().nullish(),
        payable: z.boolean().nullish(),
        payType: z.string().nullish(),
        billable: z.boolean().nullish(),
        roundHours: z.boolean().nullish(),
        mileage: z.boolean().nullish(),
        surcharge: z.boolean().nullish(),
        additionalPay: z.boolean().nullish(),
        forms: z.array(z.string()).min(1, 'Please select at least one form'),
    })
    .superRefine((data, ctx) => {
        if (data.billable) {
            if (!data.revenueCodeId) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['revenueCodeId'],
                    message: 'Revenue code is required',
                });
            }

            if (!data.serviceCodeId) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['serviceCodeId'],
                    message: 'Service code is required',
                });
            }
        }

        if (data.payable) {
            if (!data.defaultPay) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['defaultPay'],
                    message: 'Default pay is required',
                });
            }
        }
    });

const statusCodeList = createListCollection({
    items: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
    ],
});

const disciplineCodeList = createListCollection({
    items: [
        { value: 'SN', label: 'Nursing Care Services' },
        { value: 'PT', label: 'Physical Therapy Services' },
        { value: 'OT', label: 'Occupational Therapy Services' },
        { value: 'ST', label: 'Speech Pathology Services' },
        { value: 'MSW', label: 'Medical Social Services' },
        { value: 'HHA', label: 'Home Health Aide Services' },
    ],
});

const classCodes = [
    { value: 'RN', label: 'RN', discipline: 'SN' },
    { value: 'LVN', label: 'LVN', discipline: 'SN' },
    { value: 'LPN', label: 'LPN', discipline: 'SN' },
    { value: 'PT', label: 'PT', discipline: 'PT' },
    { value: 'OT', label: 'OT', discipline: 'OT' },
    { value: 'OTA', label: 'OTA', discipline: 'OTA' },
    { value: 'ST', label: 'ST', discipline: 'ST' },
    { value: 'STA', label: 'STA', discipline: 'STA' },
    { value: 'MSW', label: 'MSW', discipline: 'MSW' },
    { value: 'HHA', label: 'HHA', discipline: 'HHA' },
    { value: 'CHHA', label: 'CHHA', discipline: 'CHA' },
    { value: 'CNA', label: 'CNA', discipline: 'CNA' },
];

export function VisitTypeForm() {
    const navigate = useNavigate();
    const params = useParams<'visitTypeId'>();
    const rhf = useForm<CreateVisitTypeDto>({
        defaultValues: {
            type: '',
            title: '',
            status: '',
            discipline: '',
            employeeClass: '',
            revenueCodeId: null,
            serviceCodeId: null,
            defaultPay: null,
            defaultHours: null,
            adminCost: null,
            payable: null,
            payType: null,
            billable: null,
            roundHours: null,
            mileage: null,
            surcharge: null,
            additionalPay: null,
            forms: [],
        },
        resolver: zodResolver(validationSchema),
        reValidateMode: 'onChange',
    });

    const isPayable = useWatch({ control: rhf.control, name: 'payable' });
    const discipline = useWatch({ control: rhf.control, name: 'discipline' });
    const billable = useWatch({ control: rhf.control, name: 'billable' });

    // fetch data
    useQuery<VisitTypeDto | void>({
        queryKey: ['visit-type', params.visitTypeId],
        queryFn: async () => {
            try {
                const { data, status } = await api.visitType.findOne(params.visitTypeId as string);

                if (status !== 200 || !data) {
                    navigate('/settings/visit-types');
                }

                rhf.reset({ ...data, forms: data.forms.map((form) => form.id) });

                return data;
            } catch {
                // in case visit type is not found
                navigate('/settings/visit-types');
            }
        },
        enabled: Boolean(params.visitTypeId),
    });

    const { data: forms } = useQuery<FormDto[]>({
        queryKey: ['forms'],
        queryFn: async () => {
            const { data } = await api.form.findAll();

            return data;
        },
    });

    const { data: revenueCodes, isPending: isRevenueLoading } = useQuery<RevenueCodeDto[]>({
        queryKey: ['revenue-code', 'list'],
        queryFn: async () => {
            const { data } = await api.visitType.revenueCodes();

            return data;
        },
    });

    const { data: serviceCodes } = useQuery<ServiceCodeDto[]>({
        queryKey: ['service-code', 'list'],
        queryFn: async () => {
            const { data } = await api.visitType.serviceCodes();

            return data;
        },
    });

    const classCodeList = useMemo(() => {
        const collection = classCodes.filter((code) => code.discipline === discipline).map(({ discipline: _, ...rest }) => ({ ...rest }));
        return createListCollection({
            items: collection,
        });
    }, [discipline]);

    const revenueCodesList = useMemo(() => {
        return (
            revenueCodes
                ?.filter((rev) => rev.discipline === discipline)
                ?.map(({ id, code, description, discipline }) => ({
                    value: id,
                    label: `${discipline}${code ? ` - ${code}` : ''}: ${description}`,
                })) || []
        );
    }, [revenueCodes, discipline]);

    const serviceCodesList = useMemo(() => {
        return (
            serviceCodes
                ?.filter((service) => service.discipline === discipline)
                ?.map(({ id, code, description, discipline }) => ({
                    value: id,
                    label: `${discipline}${code ? ` - ${code}` : ''}: ${description}`,
                })) || []
        );
    }, [serviceCodes, discipline]);

    // create data
    const { mutate: mutateCreate, isPending: isCreatePending } = useMutation<unknown, unknown, CreateVisitTypeDto>({
        mutationFn: async (data) => {
            return await api.visitType.create(data);
        },
        onSuccess: () => handleSuccess(),
    });

    // update data
    const { mutate: mutateUpdate, isPending: isUpdatePending } = useMutation<unknown, unknown, UpdateVisitTypeDto>({
        mutationFn: async (data) => {
            if (!params.visitTypeId) {
                return;
            }

            return api.visitType.update(params?.visitTypeId, data);
        },
        onSuccess: () => handleSuccess(),
    });

    const handleSuccess = () => {
        navigate('/settings/visit-types');
    };

    const isSaving = isCreatePending || isUpdatePending;

    const handleSubmit = (data: CreateVisitTypeDto | UpdateVisitTypeDto) => {
        if (params?.visitTypeId) {
            mutateUpdate(data as UpdateVisitTypeDto);
        } else {
            mutateCreate(data as CreateVisitTypeDto);
        }
    };

    return (
        <FormProvider {...rhf}>
            <form noValidate onSubmit={rhf.handleSubmit((values) => handleSubmit(values))}>
                <Flex gap="8" flexDirection="column" width="100%">
                    <Flex gap="3" flexDir="column">
                        <Text fontSize="lg">Visit Details</Text>
                        <Card>
                            <FieldGroup>
                                <FieldRow>
                                    <Field
                                        label="Type"
                                        required
                                        invalid={!!rhf.formState.errors.type}
                                        errorText={rhf.formState.errors.type?.message}>
                                        <Controller
                                            name="type"
                                            render={({ field: { name, onChange, value, onBlur } }) => (
                                                <SegmentedControl
                                                    size="md"
                                                    onBlur={onBlur}
                                                    name={name}
                                                    value={value}
                                                    items={[
                                                        { value: 'home healthcare', label: 'Home Healthcare' },
                                                        { value: 'hospice', label: 'Hospice' },
                                                        { value: 'PCA', label: 'PCA' },
                                                    ]}
                                                    onValueChange={({ value }) => onChange(value)}
                                                />
                                            )}
                                        />
                                    </Field>
                                </FieldRow>
                                <FieldRow>
                                    <Field
                                        label="Visit Title"
                                        required
                                        invalid={!!rhf.formState.errors.title}
                                        errorText={rhf.formState.errors.title?.message}>
                                        <Input {...rhf.register('title')} />
                                    </Field>

                                    <Field
                                        label="Status"
                                        required
                                        invalid={!!rhf.formState.errors.status}
                                        errorText={rhf.formState.errors.status?.message}>
                                        <Controller
                                            control={rhf.control}
                                            name="status"
                                            render={({ field }) => (
                                                <SelectRoot
                                                    name={field.name}
                                                    ref={field.ref}
                                                    value={[field.value]}
                                                    onValueChange={({ value }) => {
                                                        field.onChange(value[0]);
                                                    }}
                                                    onInteractOutside={() => field.onBlur()}
                                                    collection={statusCodeList}>
                                                    <SelectTrigger>
                                                        <SelectValueText />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {statusCodeList.items.map((item) => (
                                                            <SelectItem item={item} key={item.value}>
                                                                {item.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </SelectRoot>
                                            )}
                                        />
                                    </Field>
                                </FieldRow>
                                <FieldRow>
                                    <Field
                                        label="Discipline"
                                        required
                                        invalid={!!rhf.formState.errors.discipline}
                                        errorText={rhf.formState.errors.discipline?.message}>
                                        <Controller
                                            control={rhf.control}
                                            name="discipline"
                                            render={({ field }) => (
                                                <SelectRoot
                                                    name={field.name}
                                                    ref={field.ref}
                                                    value={[field.value]}
                                                    onValueChange={({ value }) => {
                                                        field.onChange(value[0]);
                                                    }}
                                                    onInteractOutside={() => field.onBlur()}
                                                    collection={disciplineCodeList}>
                                                    <SelectTrigger>
                                                        <SelectValueText />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {disciplineCodeList.items.map((item) => (
                                                            <SelectItem item={item} key={item.value}>
                                                                {item.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </SelectRoot>
                                            )}
                                        />
                                    </Field>
                                    <Field
                                        label="Employee Class"
                                        required
                                        disabled={!discipline}
                                        invalid={!!rhf.formState.errors.employeeClass}
                                        errorText={rhf.formState.errors.employeeClass?.message}>
                                        <Controller
                                            control={rhf.control}
                                            name="employeeClass"
                                            render={({ field }) => (
                                                <SelectRoot
                                                    name={field.name}
                                                    ref={field.ref}
                                                    value={[field.value]}
                                                    onValueChange={({ value }) => {
                                                        field.onChange(value[0]);
                                                    }}
                                                    onInteractOutside={() => field.onBlur()}
                                                    collection={classCodeList}>
                                                    <SelectTrigger>
                                                        <SelectValueText />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {classCodeList.items.map((item) => (
                                                            <SelectItem item={item} key={item.value}>
                                                                {item.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </SelectRoot>
                                            )}
                                        />
                                    </Field>
                                </FieldRow>
                                <FieldRow>
                                    <Field
                                        label="Revenue Code"
                                        disabled={!discipline}
                                        invalid={!!rhf.formState.errors.revenueCodeId}
                                        errorText={rhf.formState.errors.revenueCodeId?.message}
                                        required={!!billable}>
                                        <Controller
                                            control={rhf.control}
                                            name="revenueCodeId"
                                            render={({ field: { onChange, value, name } }) => {
                                                return (
                                                    <Combobox
                                                        items={revenueCodesList}
                                                        disabled={!discipline}
                                                        name={name}
                                                        onChange={onChange}
                                                        value={revenueCodesList.find((item) => item.value === value)}
                                                        renderItem={(item) => item.label}
                                                        loading={isRevenueLoading}
                                                    />
                                                );
                                            }}
                                        />
                                    </Field>

                                    <Field
                                        label="Service Type"
                                        invalid={!!rhf.formState.errors.serviceCodeId}
                                        errorText={rhf.formState.errors.serviceCodeId?.message}
                                        required={!!billable}>
                                        <Controller
                                            control={rhf.control}
                                            name="serviceCodeId"
                                            render={({ field: { onChange, value, name } }) => {
                                                return (
                                                    <Combobox
                                                        items={serviceCodesList}
                                                        disabled={!discipline}
                                                        name={name}
                                                        onChange={onChange}
                                                        value={serviceCodesList.find((item) => item.value === value)}
                                                        renderItem={(item) => item.label}
                                                        loading={isRevenueLoading}
                                                    />
                                                );
                                            }}
                                        />
                                    </Field>
                                </FieldRow>

                                <FieldRow>
                                    <Field
                                        label="Default Pay"
                                        flex="0 1 140px"
                                        invalid={!!rhf.formState.errors.defaultPay}
                                        errorText={rhf.formState.errors.defaultPay?.message}
                                        required={!!isPayable}>
                                        <Controller
                                            control={rhf.control}
                                            name="defaultPay"
                                            render={({ field }) => (
                                                <NumberInputRoot
                                                    size="sm"
                                                    disabled={field.disabled}
                                                    name={field.name}
                                                    value={(field.value || '') as string}
                                                    onValueChange={({ value }) => field.onChange(parseFloat(value || '0'))}
                                                    width="100%">
                                                    <InputGroup
                                                        width="100%"
                                                        startElement={
                                                            <Icon size="xs">
                                                                <DollarSign />
                                                            </Icon>
                                                        }>
                                                        <NumberInputField />
                                                    </InputGroup>
                                                </NumberInputRoot>
                                            )}
                                        />
                                    </Field>
                                    <Field
                                        label="Default Hours"
                                        flex="0 1 90px"
                                        invalid={!!rhf.formState.errors.defaultHours}
                                        errorText={rhf.formState.errors.defaultHours?.message}>
                                        <Controller
                                            control={rhf.control}
                                            name="defaultHours"
                                            render={({ field }) => (
                                                <NumberInputRoot
                                                    size="sm"
                                                    disabled={field.disabled}
                                                    name={field.name}
                                                    value={(field.value || '') as string}
                                                    onValueChange={({ value }) => field.onChange(parseFloat(value || '0'))}
                                                    width="100%">
                                                    <NumberInputField />
                                                </NumberInputRoot>
                                            )}
                                        />
                                    </Field>
                                    <Field
                                        label="Admint Cost"
                                        flex="0 1 140px"
                                        invalid={!!rhf.formState.errors.adminCost}
                                        errorText={rhf.formState.errors.adminCost?.message}>
                                        <Controller
                                            control={rhf.control}
                                            name="adminCost"
                                            render={({ field }) => (
                                                <NumberInputRoot
                                                    size="sm"
                                                    disabled={field.disabled}
                                                    name={field.name}
                                                    value={(field.value || '') as string}
                                                    onValueChange={({ value }) => field.onChange(parseFloat(value || '0'))}
                                                    width="100%">
                                                    <InputGroup
                                                        width="100%"
                                                        startElement={
                                                            <Icon size="xs">
                                                                <DollarSign />
                                                            </Icon>
                                                        }>
                                                        <NumberInputField />
                                                    </InputGroup>
                                                </NumberInputRoot>
                                            )}
                                        />
                                    </Field>
                                </FieldRow>
                            </FieldGroup>
                        </Card>
                    </Flex>

                    <Flex gap="3" flexDir="column">
                        <Text fontSize="lg">Options</Text>
                        <Card>
                            <FieldGroup>
                                <Fieldset.Root>
                                    <Fieldset.Content>
                                        <Field>
                                            <Controller
                                                control={rhf.control}
                                                name="payable"
                                                render={({ field: { value, onChange } }) => {
                                                    return (
                                                        <Checkbox
                                                            onCheckedChange={({ checked }) => {
                                                                onChange(checked);
                                                                if (!checked) {
                                                                    rhf.setValue('payType', undefined);
                                                                }
                                                            }}
                                                            checked={value || undefined}>
                                                            <Text>Include in Payroll</Text>
                                                        </Checkbox>
                                                    );
                                                }}
                                            />
                                        </Field>
                                        {isPayable && (
                                            <Field pl="7">
                                                <Controller
                                                    name="payType"
                                                    render={({ field: { name, onChange, value, onBlur } }) => (
                                                        <SegmentedControl
                                                            onBlur={onBlur}
                                                            deselectable
                                                            name={name}
                                                            value={value}
                                                            items={[
                                                                { value: 'visit', label: 'Visit' },
                                                                { value: 'hours', label: 'Hours' },
                                                            ]}
                                                            onValueChange={({ value }) => onChange(value)}
                                                        />
                                                    )}
                                                />
                                            </Field>
                                        )}
                                        <Field>
                                            <Controller
                                                control={rhf.control}
                                                name="billable"
                                                render={({ field: { value, onChange } }) => {
                                                    return (
                                                        <Checkbox
                                                            onCheckedChange={({ checked }) => onChange(checked)}
                                                            checked={value || undefined}>
                                                            <Text>Billable</Text>
                                                        </Checkbox>
                                                    );
                                                }}
                                            />
                                        </Field>

                                        <Field>
                                            <Controller
                                                control={rhf.control}
                                                name="roundHours"
                                                render={({ field: { value, onChange } }) => {
                                                    return (
                                                        <Checkbox
                                                            onCheckedChange={({ checked }) => onChange(checked)}
                                                            checked={value || undefined}>
                                                            <Text>Round Hours</Text>
                                                        </Checkbox>
                                                    );
                                                }}
                                            />
                                        </Field>

                                        <Field>
                                            <Controller
                                                control={rhf.control}
                                                name="mileage"
                                                render={({ field: { value, onChange } }) => {
                                                    return (
                                                        <Checkbox
                                                            onCheckedChange={({ checked }) => onChange(checked)}
                                                            checked={value || undefined}>
                                                            <Text>Get Mileage</Text>
                                                        </Checkbox>
                                                    );
                                                }}
                                            />
                                        </Field>

                                        <Field>
                                            <Controller
                                                control={rhf.control}
                                                name="surcharge"
                                                render={({ field: { value, onChange } }) => {
                                                    return (
                                                        <Checkbox
                                                            onCheckedChange={({ checked }) => onChange(checked)}
                                                            checked={value || undefined}>
                                                            <Text>Surcharge</Text>
                                                        </Checkbox>
                                                    );
                                                }}
                                            />
                                        </Field>

                                        <Field>
                                            <Controller
                                                control={rhf.control}
                                                name="additionalPay"
                                                render={({ field: { value, onChange } }) => {
                                                    return (
                                                        <Checkbox
                                                            onCheckedChange={({ checked }) => onChange(checked)}
                                                            checked={value || undefined}>
                                                            <Text>Get Additional Pay</Text>
                                                        </Checkbox>
                                                    );
                                                }}
                                            />
                                        </Field>
                                    </Fieldset.Content>
                                </Fieldset.Root>
                            </FieldGroup>
                        </Card>
                    </Flex>

                    <Flex gap="3" flexDir="column">
                        <Text fontSize="lg">Forms</Text>
                        <Card type="standard" barType={rhf.formState.errors.forms ? 'error' : 'standard'}>
                            <Fieldset.Root invalid={!!rhf.formState.errors.forms}>
                                <Fieldset.Content>
                                    <FieldGroup>
                                        <FieldRow>
                                            <Fieldset.Root>
                                                <Fieldset.Content>
                                                    <Controller
                                                        name="forms"
                                                        control={rhf.control}
                                                        render={({ field: { onChange, value, ...rest } }) => {
                                                            return (
                                                                <CheckboxGroup
                                                                    onValueChange={onChange}
                                                                    value={value as unknown as string[]}
                                                                    {...rest}>
                                                                    <Fieldset.Content>
                                                                        <Grid
                                                                            templateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }}
                                                                            gap="8px">
                                                                            {forms?.map((form) => (
                                                                                <GridItem key={form.id}>
                                                                                    <Checkbox key={form.id} value={form.id}>
                                                                                        <Text fontSize="14px">{form.name}</Text>
                                                                                    </Checkbox>
                                                                                </GridItem>
                                                                            ))}
                                                                        </Grid>
                                                                    </Fieldset.Content>
                                                                </CheckboxGroup>
                                                            );
                                                        }}
                                                    />
                                                </Fieldset.Content>
                                            </Fieldset.Root>
                                        </FieldRow>
                                    </FieldGroup>
                                </Fieldset.Content>
                                <Fieldset.ErrorText>{rhf.formState.errors.forms?.message}</Fieldset.ErrorText>
                            </Fieldset.Root>
                        </Card>
                    </Flex>

                    <Flex alignItems="flex-start" gap="4">
                        <Button type="submit" loading={isSaving} loadingText={params.visitTypeId ? 'Saving' : 'Creating'}>
                            {params.visitTypeId ? 'Save' : 'Create'}
                        </Button>
                        <Link to="/settings/visit-types">
                            <Button type="button" variant="outline">
                                Cancel
                            </Button>
                        </Link>
                    </Flex>
                </Flex>
            </form>
        </FormProvider>
    );
}
